/**
 * AuthSwitch — render one of two children based on session presence.
 *
 * Used for routes that need a different face for signed-in vs signed-out
 * visitors at the SAME URL (e.g. `/catalogue` shows the authenticated
 * CataloguePage to members, the marketing CatalogueMarketingPage to guests).
 *
 * Loading state: render `signedOut` so first paint matches the marketing
 * variant — guests outnumber members on public routes, the signed-out view
 * is cheaper to render, and the swap when `/api/me` resolves to a user is
 * fast enough that authenticated members tolerate the brief flash. If a
 * route demands no flash, wrap children in a Suspense boundary instead.
 */

import { type ReactElement, useEffect, useState } from 'react';
import { authApi } from '@/state/auth/api';

interface AuthSwitchProps {
  signedIn: ReactElement;
  signedOut: ReactElement;
}

type AuthState = 'loading' | 'signed-in' | 'signed-out';

export function AuthSwitch({ signedIn, signedOut }: AuthSwitchProps): ReactElement {
  const [state, setState] = useState<AuthState>('loading');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await authApi.me();
      if (cancelled) return;
      if (res.ok && res.data) {
        setState('signed-in');
      } else {
        setState('signed-out');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'signed-in') return signedIn;
  return signedOut;
}
